import { FC, Fragment, useState } from 'react';
import {
	Checkbox,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Select,
	Typography,
	Upload,
} from 'antd';
import { LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { TechnicalAspect } from '@/libs/fleet-api/services/types';
import rule, { rules } from '@/libs/antd/rules';
import {
	useSelectAddress,
	useSelectOfficeOwnershipStatus,
} from '@/libs/fleet-api/hooks/master-data';
import { AddressMasterData } from '@/libs/fleet-api/services/master-data';
import dayjs from 'dayjs';
import { OfficeOwnershipStatus } from '@/libs/fleet-api/enums';
import { inputCurrency } from '@/libs/antd/input-number';
import { useMutation } from '@tanstack/react-query';
import { uploader } from '@/libs/fleet-api/uploader';
import { UploadFile } from 'antd/es/upload';
import { submitCCAOfficeObservationTechnicalAspect } from '@/libs/fleet-api/services/visit-reports';
import { FileData } from '@/libs/fleet-api/services/customer';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useCCAVisitReportVerificationPage } from './VisitReportVerificationPage';

export type TechnicalAspectForm = TechnicalAspect<'payload'> &
	Pick<TechnicalAspect, 'unitDocumentFile'>;

export const useCCAVisitReportForm = () => {
	const technicalAspectForm = useForm<TechnicalAspectForm>();
	const technicalAspectMutation = useMutation({
		mutationFn: submitCCAOfficeObservationTechnicalAspect,
	});

	return {
		technicalAspectForm,
		technicalAspectMutation,
	};
};

const CCAVisitReportForm: FC<ReturnType<typeof useCCAVisitReportForm>> = ({
	technicalAspectForm,
	technicalAspectMutation,
}) => {
	const { form, fieldName } = technicalAspectForm;
	const {
		officeObservation,
		surveyInformation,
		customerAddress,
		mainInformation,
	} = useCCAVisitReportVerificationPage();

	const { technicalAspect } = officeObservation ?? {};
	const selectAddress = useSelectAddress();
	const [selectedAddress, setSelectedAddress] =
		useState<AddressMasterData | null>(
			technicalAspect?.officePostalCode ?? null,
		);
	const selectOfficeOwnershipStatus = useSelectOfficeOwnershipStatus();

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const isSameAddress = Form.useWatch(
		['isOfficeAddressSameAsCustomer'],
		technicalAspectForm,
	);

	const handleSameAddress = (e: CheckboxChangeEvent) => {
		if (e.target.checked && customerAddress) {
			form.setFieldsValue({
				officeAddress: customerAddress?.address,
				officeRt: customerAddress?.rt,
				officeRw: customerAddress?.rw,
				officePostalCodeId: customerAddress?.postalDetail.postalId,
			});
			setSelectedAddress(customerAddress?.postalDetail);
		}
		form.setFieldValue(
			fieldName(['isOfficeAddressSameAsCustomer']),
			e.target.checked,
		);
	};

	return (
		<Form
			{...ANTD_DEFAULT_PROPS.FORM}
			name="technicalAspect"
			form={form}
			labelCol={{
				span: 10,
			}}
			initialValues={
				{
					officeAddress: technicalAspect?.officeAddress,
					officeRt: technicalAspect?.officeRt,
					officeRw: technicalAspect?.officeRw,
					officePostalCodeId: technicalAspect?.officePostalCode?.postalId,
					officePhoneNo: technicalAspect?.officePhoneNo,
					officeOwnershipStatusId: technicalAspect?.officeOwnershipStatus?.id,
					officeOccupationDate: technicalAspect?.officeOccupationDate,
					officeRentalStartDate: technicalAspect?.officeRentalStartDate,
					officeRentalEndDate: technicalAspect?.officeRentalEndDate,
					officeAnnualRentalFee: technicalAspect?.officeAnnualRentalFee,
					officeHeadcount: technicalAspect?.officeHeadcount,
					startedBusinessYear: technicalAspect?.startedBusinessYear,
					previousBusiness: technicalAspect?.previousBusiness,
					companyEstablishmentDate: technicalAspect?.companyEstablishmentDate,
					branchLocation: technicalAspect?.branchLocation,
					numberCurrentBranch: technicalAspect?.numberCurrentBranch,
					unitDocumentFile: technicalAspect?.unitDocumentFile,
					isOfficeAddressSameAsCustomer:
						technicalAspect?.isOfficeAddressSameAsCustomer,
				} as TechnicalAspectForm
			}
			onFinish={(values) => {
				technicalAspectMutation.mutateAsync({
					...values,
					surveyId: surveyInformation!.id!,
					id: surveyInformation!.id!,
					unitDocumentFile: values.unitDocumentFile && {
						id: values.unitDocumentFile.id,
						name: values.unitDocumentFile.name,
						path: values.unitDocumentFile.path,
					},
				});
			}}
		>
			<Row
				gutter={[20, 40]}
				align="middle"
				style={{
					marginBottom: 24,
				}}
			>
				<Col span={24}>
					<Form.Item name={fieldName('isOfficeAddressSameAsCustomer')}>
						<Checkbox
							checked={isSameAddress}
							onChange={handleSameAddress}
						>
							Checklist kotak disamping apabila{' '}
							<strong style={{ color: '#1557BC' }}>Alamat Usaha</strong> juga
							merupakan{' '}
							<strong style={{ color: '#1557BC' }}>
								Alamat Perusahaan sesuai NPWP
							</strong>
						</Checkbox>
					</Form.Item>
				</Col>
			</Row>
			<Row gutter={[20, 40]}>
				<Col span={24}>
					<Row gutter={[20, 40]}>
						<Col span={24}>
							<Form.Item
								name={fieldName('officeAddress')}
								label="Alamat"
								labelCol={{ span: 5 }}
								rules={[rule.required()]}
								style={{
									marginLeft: -5,
								}}
							>
								<Input disabled={isSameAddress} />
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={[20, 40]}>
						<Col span={12}>
							<Form.Item
								label="RT/RW"
								required
							>
								<Row
									wrap={false}
									gutter={8}
								>
									<Col flex="auto">
										<Form.Item
											name={fieldName('officeRt')}
											noStyle
											rules={rules.rt}
										>
											<Input disabled={isSameAddress} />
										</Form.Item>
									</Col>
									<Col flex="none">-</Col>
									<Col flex="auto">
										<Form.Item
											name={fieldName('officeRw')}
											noStyle
											rules={rules.rw}
										>
											<Input disabled={isSameAddress} />
										</Form.Item>
									</Col>
								</Row>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name={fieldName('officePostalCodeId')}
								label="Kode Pos"
								rules={[rule.required()]}
							>
								<Select
									{...selectAddress}
									options={
										selectAddress.options?.length
											? selectAddress.options
											: selectedAddress?.postalId
												? [
														{
															label: selectedAddress?.postalCode,
															value: selectedAddress?.postalId,
														},
													]
												: []
									}
									onSelect={(value, option) => {
										selectAddress.onSelect!(value, option);
										setSelectedAddress(option.data);
									}}
									disabled={isSameAddress}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={[20, 40]}>
						<Col span={12}>
							<Form.Item label="Kelurahan">
								<Input
									disabled
									value={selectedAddress?.locationName ?? '-'}
								/>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="Kecamatan">
								<Input
									disabled
									value={selectedAddress?.districtName ?? '-'}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={[20, 40]}>
						<Col span={12}>
							<Form.Item label="Kota">
								<Input
									disabled
									value={selectedAddress?.cityName ?? '-'}
								/>
							</Form.Item>
						</Col>

						<Col span={12}>
							<Form.Item
								label="No. Telepon"
								name={fieldName(['officePhoneNo'])}
								rules={[rule.required()]}
							>
								<Input />
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={[20, 40]}>
						<Col span={12}>
							<Form.Item
								name={fieldName(['startedBusinessYear'])}
								label="Tahun Memulai Usaha"
								getValueProps={(value) => ({
									value: value && dayjs(value),
								})}
								normalize={(value) =>
									value && dayjs(value).utc(true).toISOString()
								}
								rules={[rule.required()]}
							>
								<DatePicker
									disabledDate={(now) => now > dayjs().subtract(1, 'day')}
								/>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name={fieldName(['previousBusiness'])}
								label="Usaha Sebelumnya (Jika ada)"
							>
								<Input />
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={[20, 40]}>
						<Col span={12}>
							<Form.Item
								name={fieldName(['companyEstablishmentDate'])}
								label="Tanggal Pendirian Perusahaan"
								getValueProps={(value) => ({
									value: value && dayjs(value),
								})}
								normalize={(value) =>
									value && dayjs(value).utc(true).toISOString()
								}
								rules={[rule.required()]}
							>
								<DatePicker
									disabledDate={(now) => now > dayjs().subtract(1, 'day')}
								/>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name={fieldName(['officeHeadcount'])}
								label="Jumlah Karyawan Saat Ini"
								rules={[rule.required()]}
							>
								<InputNumber min={0} />
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={[20, 40]}>
						<Col span={12}>
							<Form.Item
								name={fieldName(['numberCurrentBranch'])}
								label="Jumlah Kantor Cabang Saat Ini"
								rules={[rule.required()]}
							>
								<InputNumber min={0} />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name={fieldName(['branchLocation'])}
								label="Lokasi Kantor Cabang"
								rules={[rule.required()]}
							>
								<Input />
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row
						gutter={[20, 40]}
						align="top"
					>
						<Col span={12}>
							<Form.Item
								name={fieldName(['officeOwnershipStatusId'])}
								label="Status Kepemilikan Kantor"
								rules={[rule.required()]}
							>
								<Select
									{...selectOfficeOwnershipStatus}
									options={
										!technicalAspect?.officeOwnershipStatus ||
										selectOfficeOwnershipStatus.options?.length
											? selectOfficeOwnershipStatus.options
											: [
													{
														label: technicalAspect?.officeOwnershipStatus.value,
														value: technicalAspect?.officeOwnershipStatus.id,
													},
												]
									}
								/>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item shouldUpdate>
								{({ getFieldValue }) => {
									const status = getFieldValue(
										fieldName(['officeOwnershipStatusId']),
									) as OfficeOwnershipStatus | undefined;

									if (!status) return null;

									if (status === OfficeOwnershipStatus.Sewa) {
										return (
											<Fragment>
												<Form.Item
													label="Periode Sewa"
													labelCol={{
														span: 10,
														style: { marginBottom: 20 },
													}}
													required
												>
													<Row gutter={[20, 40]}>
														<Col span={12}>
															<Form.Item
																name={fieldName(['officeRentalStartDate'])}
																getValueProps={(value) => ({
																	value: value && dayjs(value),
																})}
																normalize={(value) =>
																	value && dayjs(value).utc(true).toISOString()
																}
																rules={[rule.required()]}
																noStyle
															>
																<DatePicker
																	suffixIcon={null}
																	placeholder="YYYY-MM-DD"
																/>
															</Form.Item>
														</Col>
														<Col span={12}>
															<Form.Item
																name={fieldName(['officeRentalEndDate'])}
																getValueProps={(value) => ({
																	value: value && dayjs(value),
																})}
																normalize={(value) =>
																	value && dayjs(value).utc(true).toISOString()
																}
																rules={[rule.required()]}
																noStyle
															>
																<DatePicker
																	suffixIcon={null}
																	placeholder="YYYY-MM-DD"
																	disabled={
																		!getFieldValue(
																			fieldName(['officeRentalStartDate']),
																		)
																	}
																	disabledDate={(now) =>
																		now <
																		dayjs(
																			getFieldValue(
																				fieldName(['officeRentalStartDate']),
																			),
																		)
																	}
																/>
															</Form.Item>
														</Col>
													</Row>
												</Form.Item>
												<Form.Item
													name={fieldName(['officeAnnualRentalFee'])}
													label="Biaya Sewa per Tahun Sebesar"
													labelCol={{
														span: 10,
														style: { marginBottom: 20 },
													}}
													rules={[rule.required()]}
												>
													<InputNumber {...inputCurrency} />
												</Form.Item>
											</Fragment>
										);
									}

									return (
										<Form.Item
											name={fieldName(['officeOccupationDate'])}
											label="Mulai Menempati Sejak"
											getValueProps={(value) => ({
												value: value && dayjs(value),
											})}
											normalize={(value) =>
												value && dayjs(value).utc(true).toISOString()
											}
											rules={[rule.required()]}
											style={{ paddingInline: 10 }}
											labelCol={{ span: 10 }}
										>
											<DatePicker.YearPicker />
										</Form.Item>
									);
								}}
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Typography.Title level={3}>Unit yang Dimiliki</Typography.Title>
					<Row
						gutter={[20, 40]}
						align="middle"
					>
						<Col span={12}>
							<Form.Item
								label="Upload Dokumen Unit"
								name={fieldName(['unitDocumentFile'])}
								rules={[rule.required()]}
								getValueProps={(value) => {
									const file = value as FileData;
									return {
										fileList: file?.id
											? ([
													{
														uid: String(file.id),
														name: file.name,
														thumbUrl: file.url,
													},
												] as UploadFile[])
											: [],
									};
								}}
								normalize={({ file }: { file: UploadFile }) => {
									return {
										id: Number(file.uid),
										format: file.type,
										name: file.name,
										url: file.thumbUrl,
									} as FileData;
								}}
							>
								<Upload.Dragger
									listType="picture"
									maxCount={1}
									beforeUpload={() => false}
									onRemove={() => {
										form.setFieldValue(fieldName(['unitDocumentFile']), null);
										return false;
									}}
									onChange={async ({ file }) => {
										const fileid = Date.now().toString();
										const filepath = `customer/${mainInformation!.customerId}/${mainInformation!.prospectId}/unit-document`;

										try {
											const url = await uploadMutation.mutateAsync({
												file: file as unknown as File,
												fileid,
												filepath,
											});

											form.setFieldValue(fieldName(['unitDocumentFile']), {
												format: 'jpeg',
												id: fileid,
												name: file.name,
												path: filepath,
												url,
											});
										} catch {
											form.setFieldValue(fieldName(['unitDocumentFile']), null);
										}
									}}
								>
									{uploadMutation.isPending ? (
										<LoadingOutlined
											style={{
												fontSize: 25,
												color: '#BABABA',
												display: 'block',
											}}
										/>
									) : (
										<UploadOutlined
											style={{
												fontSize: 25,
												color: '#BABABA',
												display: 'block',
											}}
										/>
									)}
								</Upload.Dragger>
							</Form.Item>
						</Col>
					</Row>
				</Col>
			</Row>
			<Divider />
		</Form>
	);
};

export default CCAVisitReportForm;
